/**
 * A variable that remembers ALTERNATIVES — `categories` holding books, movies
 * and music, with one of them active.
 *
 * ## The shape, and why it is additive
 *
 * A variable is `{ key, value, enabled }` everywhere in this app: five scope
 * stores, the sync engine, the collection runner, the code generators. Options
 * are a FOURTH field, never a replacement:
 *
 *     { key: 'categories', value: 'books', enabled: true,
 *       options: ['books', 'movies', 'music'] }
 *
 * `value` is still the active value and still a plain string, so resolution,
 * nesting, cycle detection and every existing consumer are untouched — anything
 * that ignores `options` keeps working exactly as before. That is what makes
 * this safe to add without a migration: an older build reading a newer variable
 * simply sees a normal variable, and a newer build reading an older one sees a
 * variable with no alternatives.
 *
 * ## What this is NOT
 *
 * Not an array that resolves to many values. `{{categories}}` yields ONE string,
 * the active one. Iterating a request over every option is a different feature
 * (it changes the runner and the response pane) and deliberately not this.
 */

/** The options of a variable, always an array of non-empty strings. */
export function readOptions(variable) {
  const raw = variable?.options;
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  const out = [];
  for (const item of raw) {
    if (typeof item !== 'string') continue;
    const value = item.trim();
    if (!value || seen.has(value)) continue;   // duplicates would render twice and pick the same thing
    seen.add(value);
    out.push(value);
  }
  return out;
}

/** True when this variable offers a choice worth showing a picker for. */
export const hasOptions = (variable) => readOptions(variable).length > 0;

/**
 * Should this variable's value field be a picker?
 *
 * A SECRET never gets one, even when revealed. The menu is portaled to the body
 * and would list every alternative in plain text, which is a worse leak than the
 * masked field it sits next to — and "reveal" is a deliberate, momentary act on
 * ONE value, not consent to display the whole set. A secret keeps its masked
 * input and its alternatives stay editable in the variable editor.
 */
export const shouldOfferPicker = (variable) => !variable?.secret && hasOptions(variable);

/**
 * Add `value` to a variable's options, keeping order and rejecting duplicates
 * and blanks. Returns the new array — callers own the write.
 */
export function addOption(variable, value) {
  const next = String(value ?? '').trim();
  const current = readOptions(variable);
  if (!next || current.includes(next)) return current;
  return [...current, next];
}

/** Remove `value`. Removing the ACTIVE one is the caller's problem — see `activeAfterRemoval`. */
export function removeOption(variable, value) {
  return readOptions(variable).filter((o) => o !== value);
}

/**
 * Which value should be active once `removed` is gone.
 *
 * Removing the option that is currently active would otherwise leave the
 * variable resolving to something no longer on its own list, which reads as a
 * bug even though the value is still perfectly valid. Falls back to the first
 * remaining option, or keeps the current value when nothing is left — a
 * variable with no options is just an ordinary variable again, not an empty one.
 */
export function activeAfterRemoval(variable, removed, currentValue) {
  if (currentValue !== removed) return currentValue;
  const rest = removeOption(variable, removed);
  return rest.length ? rest[0] : currentValue;
}

/** `{value,label}` pairs for CompactSelectPicker. */
export const toPickerOptions = (variable) =>
  readOptions(variable).map((value) => ({ value, label: value }));
